import {
  useGetExternalAccountsSuspense,
  useGetTransactions,
} from 'api/payments/generated/fragmentPaymentsAPI';
import {
  PaymentViewLayout,
  PaymentViewLoading,
} from 'components/Layout/PaymentViewLayout';
import { useDocumentTitle } from 'hooks/useDocumentTitle';
import React, { Suspense, useCallback, useMemo, useState } from 'react';
import { makePath } from 'utils/routes';
import { useLocation } from 'wouter';

import { Amount } from '@fragment/ui/src/components/Amount/Amount';
import { LayoutRow } from '@fragment/ui/src/components/Payments/LayoutRow';
import type { ColumnDefinition } from '@fragment/ui/src/components/Table';
import { NavTableV2 } from '@fragment/ui/src/components/Table/NavTableV2';
import { TabsV2 } from '@fragment/ui/src/components/Tabs/TabsV2';

import { SyncTransactionModal } from './SyncTransactionModal';

type TransactionRow = {
  id: string;
  external_id: string;
  amount: string;
  currency: string;
  posted: string;
  unallocated_amount: string;
};

const transactionSchema: ColumnDefinition<TransactionRow>[] = [
  {
    title: 'ID',
    key: 'id',
    colspan: 'col-span-2',
  },
  {
    title: 'External ID',
    key: 'external_id',
    colspan: 'col-span-2',
  },
  {
    title: 'Posted',
    colspan: 'col-span-2',
    format: (row: TransactionRow) => (
      <span>{new Date(row.posted).toLocaleDateString()}</span>
    ),
  },
  {
    title: 'Amount',
    colspan: 'col-span-1',
    format: (row: TransactionRow) => (
      <Amount
        amount={row.amount}
        currency={{
          code: row.currency as 'USD' | 'EUR' | 'GBP',
          name: row.currency,
          precision: 2,
        }}
      />
    ),
  },
  {
    title: 'Unallocated',
    justify: 'right',
    colspan: 'col-span-1',
    format: (row: TransactionRow) => (
      <Amount
        amount={row.unallocated_amount}
        currency={{
          code: row.currency as 'USD' | 'EUR' | 'GBP',
          name: row.currency,
          precision: 2,
        }}
      />
    ),
  },
];

const transactionEmptyValue = {
  ID: 'No Transactions',
  'External ID': 'N/A',
  Amount: 'N/A',
  Currency: 'N/A',
  Posted: 'N/A',
  'Unallocated Amount': 'N/A',
};

type Props = {
  workspaceId: string;
  accountId: string;
};

const AccountDetailPageContent = ({
  workspaceId,
  accountId,
}: Props): JSX.Element => {
  const [, setLocation] = useLocation();
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [selectedTab, setSelectedTab] = useState<'unreconciled' | 'reconciled'>(
    'unreconciled'
  );
  const { data: accountsData } = useGetExternalAccountsSuspense();
  const { data, isLoading } = useGetTransactions({
    account: accountId,
    reconciliation_status: selectedTab,
  });

  const handleBack = useCallback(() => {
    setLocation(
      makePath(['w', workspaceId, 'payments', 'transactions'], {
        absolute: true,
      })
    );
  }, [workspaceId, setLocation]);

  const handleCreate = useCallback(() => {
    setShowSyncModal(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setShowSyncModal(false);
  }, []);

  // Find the current account
  const account = useMemo(
    () => accountsData.data.find((acc) => acc.id === accountId),
    [accountsData.data, accountId]
  );

  useDocumentTitle(account?.name ?? 'Account Transactions');

  const transactions: TransactionRow[] = useMemo(() => {
    if (!data?.data) return [];
    return data.data.map((transaction) => ({
      id: transaction.id,
      external_id: transaction.external_id,
      amount: transaction.amount,
      currency: transaction.currency,
      posted: transaction.posted,
      unallocated_amount: transaction.unallocated_amount,
    }));
  }, [data]);

  const getPath = useCallback(
    (transaction: TransactionRow) =>
      makePath(['w', workspaceId, 'payments', 'transactions', transaction.id], {
        absolute: true,
      }),
    [workspaceId]
  );

  if (isLoading) {
    return (
      <PaymentViewLayout
        title="Transactions"
        primaryAction={{
          title: 'New',
          onClick: handleCreate,
          'data-testid': 'sync-transaction-button',
        }}
      >
        <div className="flex items-center justify-center py-f8 text-main-500">
          Loading transactions...
        </div>
      </PaymentViewLayout>
    );
  }

  return (
    <>
      <PaymentViewLayout
        title="Transactions"
        primaryAction={{
          title: 'New',
          onClick: handleCreate,
          'data-testid': 'sync-transaction-button',
        }}
      >
        <div className="flex flex-col min-h-f1 w-full h-full">
          <button
            type="button"
            onClick={handleBack}
            className="px-f2 py-f2 text-left text-main-500 hover:text-main border-b border-main-300"
          >
            ← Back to Accounts
          </button>
          {account && (
            <div className="px-f2 py-f3 border-b border-main-300">
              <div className="text-main">{account.name}</div>
              <div className="text-main-500 text-small">
                {account.external_id}
              </div>
            </div>
          )}

          <LayoutRow height="fit" isFirst>
            <TabsV2
              options={[
                { label: 'Unreconciled', value: 'unreconciled' as const },
                { label: 'Reconciled', value: 'reconciled' as const },
              ]}
              selectedValue={selectedTab}
              onClick={setSelectedTab}
            />
          </LayoutRow>
          <NavTableV2
            textSize="md"
            data={transactions}
            emptyValue={transactionEmptyValue}
            getPath={getPath}
            schema={transactionSchema}
            showHeader={false}
            stickyHeader={false}
          />
        </div>
      </PaymentViewLayout>
      {showSyncModal && (
        <SyncTransactionModal
          accountId={accountId}
          onClose={handleCloseModal}
        />
      )}
    </>
  );
};

export const AccountDetailPage = ({
  workspaceId,
  accountId,
}: Props): JSX.Element => (
  <Suspense
    fallback={<PaymentViewLoading message="Loading account details..." />}
  >
    <AccountDetailPageContent workspaceId={workspaceId} accountId={accountId} />
  </Suspense>
);
